/**
 * Invoice section — create and track this submission's Ascend invoices
 * (OR-1449).
 *
 * Billing for bound policies lives in Ascend; this page is Orion's window
 * onto it. The table is the "Invoice History" from the Figma; the create
 * flow is a separate full-screen route.
 *
 * The gate banner mirrors what the backend actually enforces before it
 * will build an Ascend Billable: a linked broker, effective/expiration
 * dates, and a policy to render the binder from. Only the first two are
 * visible on the submission detail, so the policy check stays server-side
 * and surfaces as a 422 the create screen renders.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { CheckCircle, File05, Folder, Plus, SearchLg, XCircle } from "@untitledui/icons";
import { Button, Input } from "@tricura/atlas-ui";
import folderIcon from "../../../assets/folder-icon.svg";
import {
    ApiError,
    archiveAscendInvoice,
    getAgent,
    getInvoiceDraft,
    getPremiumSelection,
    getSubmission,
    listAllAscendInvoices,
    listAscendInvoiceFiles,
    type AscendInvoice,
} from "../../../api";
import { FeaturedAlert } from "../../../components/featured-alert";
import { FloatingAlert } from "../../../components/floating-alert";
import { TableEmptyState } from "../../../components/table-empty-state";
import { useSubmissionContext } from "../context";
import { InvoiceEmptyIcon } from "./invoice-empty-icon";
import { InvoiceHistoryTable, filterInvoices, type InvoiceRowAction } from "./invoice-history-table";

type ListState =
    | { status: "loading" }
    | { status: "ready"; invoices: AscendInvoice[] }
    | { status: "error"; message: string };

/** Decimal string off the quote → number, blank and garbage alike to null. */
function toNumber(raw: string | null | undefined): number | null {
    if (raw == null || raw.trim() === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

/**
 * Whether the submission carries enough for the backend to build the
 * Ascend Billable. Deliberately narrow: it checks only what the detail
 * exposes, so it can never claim a submission is ready when it isn't, and
 * never blocks on something it can't actually see.
 */
function isReadyForInvoice(detail: ReturnType<typeof useSubmissionContext>["detail"]): boolean {
    if (!detail?.id) return false;
    const { accountBasics, brokerage } = detail;
    // The broker check is the LINK (broker_id), not the denormalized
    // name/email — seeds and older submissions carry the id with those
    // fields empty, and the id is what the backend's precondition wants.
    return Boolean(accountBasics?.renewalDate && accountBasics?.expirationDate && brokerage?.brokerId);
}

export function InvoicePage() {
    const { detail } = useSubmissionContext();
    const navigate = useNavigate();
    const submissionId = detail?.id ?? "";
    const [state, setState] = useState<ListState>({ status: "loading" });
    const [search, setSearch] = useState("");
    const [error, setError] = useState<string | null>(null);

    // The create screen hands the new invoice number over through router
    // state, so the success toast survives the navigation without a store.
    const routerState = useLocation().state as { generatedInvoiceNumber?: string } | null;
    const [generated, setGenerated] = useState<string | null>(routerState?.generatedInvoiceNumber ?? null);

    const load = useCallback(() => {
        if (!submissionId) {
            setState({ status: "ready", invoices: [] });
            return;
        }
        setState({ status: "loading" });
        // The saved draft is its own resource and does NOT come back in the
        // list, but the design shows it as a row (Draft badge, Edit action)
        // — so it is fetched alongside and synthesized into one.
        Promise.all([listAllAscendInvoices(submissionId), getInvoiceDraft(submissionId)])
            .then(async ([res, draft]) => {
                // The columns the draft resource cannot carry: they are live
                // values off the submission, the quote and the broker,
                // denormalized onto a row only when a real invoice is
                // created. Fetched here so the synthesized row below reads
                // like every other one.
                //
                // The broker is what resolves Brokerage and Broker Name: the
                // submission's own columns are denormalized and often null,
                // which is exactly how a real invoice row can name a
                // brokerage the submission itself doesn't.
                //
                // Only when a draft actually exists, so the ordinary case
                // costs no extra request; and each source independently, so
                // a missing quote blanks one cell instead of the whole row.
                const brokerId = detail?.brokerage?.brokerId ?? "";
                const [rawSubmission, quote, broker] = draft
                    ? await Promise.all([
                          getSubmission(submissionId).catch(() => null),
                          getPremiumSelection(submissionId).catch(() => null),
                          brokerId ? getAgent(brokerId).catch(() => null) : Promise.resolve(null),
                      ])
                    : [null, null, null];
                const rows = draft
                    ? [
                          {
                              id: "draft",
                              submissionId,
                              syncStatus: "draft" as const,
                              invoiceSequence: 0,
                              invoiceNumber: "",
                              dueDate: null,
                              createdAt: draft.createdAt,
                              updatedAt: draft.createdAt,
                              // Null stays null — a fallback here once masked
                              // an unsaved coverage as "Professional Liability".
                              coverageType: draft.coverageType as AscendInvoice["coverageType"],
                              ascendInvoiceStatus: null,
                              dueValue: null,
                              namedInsured: detail?.accountBasics?.fni ?? "",
                              effectiveDate: rawSubmission?.effective_date ?? null,
                              expirationDate: rawSubmission?.expiration_date ?? null,
                              brokerageName: detail?.brokerage?.brokerageName || broker?.brokerage?.name || "",
                              brokerName: detail?.brokerage?.brokerName || broker?.name || "",
                              selectedPremium: toNumber(quote?.form.inputs.selectedPremium),
                              // The list's rollups are computed per invoice
                              // row; a draft has none of them yet. The three
                              // it CAN carry come off the draft itself.
                              tricuraCommissionAmount: null,
                              agentCommissionAmount: null,
                              taxesTotal: null,
                              surchargesTotal: null,
                              tricuraCommissionTaxAmount: draft.tricuraCommissionTaxAmount,
                              agencyFeeAmount: draft.agencyFeeAmount,
                              totalCommissionAmount: draft.totalCommissionAmount,
                              totalInvoicedAmount: null,
                              ascendProgramUrl: null,
                              archivedAt: null,
                              // Nothing is billed yet, so there is no invoiced
                              // policy number or rate to show (OR-1657).
                              policyNumber: null,
                              tricuraCommissionRate: null,
                              agentCommissionRate: null,
                          },
                          ...res.items,
                      ]
                    : res.items;
                setState({ status: "ready", invoices: rows });
            })
            .catch((e: unknown) =>
                setState({ status: "error", message: e instanceof Error ? e.message : "Unknown error" }),
            );
    }, [submissionId, detail]);

    useEffect(load, [load]);

    const ready = isReadyForInvoice(detail);
    const invoices = state.status === "ready" ? state.invoices : [];

    // Search only ever hides rows, so an empty result with a non-empty
    // query is the "No Results Found" case, not the first-run empty state.
    const isSearching = search.trim().length > 0;
    const noMatches = useMemo(() => filterInvoices(invoices, search).length === 0, [invoices, search]);

    const onRowAction = useCallback(
        async (action: InvoiceRowAction, invoice: AscendInvoice) => {
            if (action === "view") {
                navigate(`/submissions/${submissionId}/invoice/${invoice.id}`);
                return;
            }
            if (action === "edit") {
                // The synthesized draft row edits through the create screen —
                // the draft IS that form, saved.
                if (invoice.id === "draft") navigate(`/submissions/${submissionId}/invoice/new`);
                else navigate(`/submissions/${submissionId}/invoice/${invoice.id}/edit`);
                return;
            }
            if (action === "open-in-ascend") {
                // The menu item is disabled without a URL, so this is defensive.
                if (invoice.ascendProgramUrl) window.open(invoice.ascendProgramUrl, "_blank", "noopener");
                return;
            }
            if (action === "archive") {
                try {
                    await archiveAscendInvoice(invoice.id);
                    // Re-list rather than patching local state: archiving can
                    // also move the Ascend billing status to void, and the
                    // row carries it.
                    load();
                } catch (e: unknown) {
                    setError(
                        e instanceof ApiError && e.status === 409
                            ? "This invoice is still syncing with Ascend. Try archiving it once the sync finishes."
                            : e instanceof Error
                              ? e.message
                              : "Couldn't archive the invoice.",
                    );
                }
                return;
            }
            if (action !== "download") return;
            try {
                const files = await listAscendInvoiceFiles(invoice.id);
                const target = files.find((f) => f.downloadUrl);
                if (!target) {
                    setError("This invoice's document isn't ready yet. Try again once the sync finishes.");
                    return;
                }
                window.open(target.downloadUrl, "_blank", "noopener");
            } catch (e: unknown) {
                setError(e instanceof Error ? e.message : "Couldn't download the invoice.");
            }
        },
        [navigate, submissionId, load],
    );

    const createDisabled = !ready;
    const goCreate = useCallback(
        () => navigate(`/submissions/${submissionId}/invoice/new`),
        [navigate, submissionId, load],
    );

    const header = useMemo(
        () => (
            <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="flex flex-col gap-1">
                    <h2 className="text-primary text-md font-semibold">Invoice</h2>
                    <p className="text-tertiary text-sm">Create and track invoices for this submission</p>
                </div>
                <Button
                    size="sm"
                    iconLeading={Plus}
                    isDisabled={createDisabled}
                    data-autotest="invoice-create-button"
                    onClick={goCreate}
                >
                    Create an Invoice
                </Button>
            </header>
        ),
        [createDisabled],
    );

    return (
        <div className="w-full space-y-6">
            {header}

            {!ready && (
                <FeaturedAlert
                    color="warning"
                    title="Complete required sections before generating an invoice"
                    description="Make sure the following sections are filled out: UW Information, Schedule, Loss Runs, Premium selection and Policy Details."
                    data-autotest="invoice-gate-alert"
                />
            )}

            {/* Outcome messages float over the content, centred; the gate
              * banner above stays in the flow. */}
            {(generated || error) && (
                <FloatingAlert>
                    {generated && (
                        <FeaturedAlert
                            color="success"
                            icon={CheckCircle}
                            className="shadow-lg"
                            title={`invoice ${generated} generated!`}
                            // ⚠️ Only half true today: the backend captures
                            // Ascend's billing status once when the sync
                            // completes and never refreshes it, so nothing
                            // updates the row afterwards. Copy is the
                            // design's; the behaviour is in the gap list.
                            description="Payment will be tracked and the status updated automatically."
                            onClose={() => setGenerated(null)}
                            data-autotest="invoice-generated-success"
                        />
                    )}
                    {error && (
                        <FeaturedAlert
                            color="error"
                            icon={XCircle}
                            className="shadow-lg"
                            title="Couldn't download the invoice"
                            description={error}
                            onClose={() => setError(null)}
                            data-autotest="invoice-download-error"
                        />
                    )}
                </FloatingAlert>
            )}

            <section className="border-secondary bg-primary shadow-xs overflow-hidden rounded-xl border">
                <header className="border-secondary flex items-center justify-end gap-3 border-b px-4 py-3 sm:px-6 sm:py-4">
                    <div className="w-full max-w-xs">
                        <Input
                            size="sm"
                            icon={SearchLg}
                            placeholder="Search"
                            value={search}
                            onChange={setSearch}
                            aria-label="Search invoices"
                            data-autotest="invoice-search-input"
                        />
                    </div>
                </header>

                {state.status === "loading" && <div className="text-tertiary px-6 py-10 text-sm">Loading invoices…</div>}

                {state.status === "error" && (
                    <div role="alert" className="text-error-primary px-6 py-10 text-sm">
                        Couldn't load invoices: {state.message}
                    </div>
                )}

                {/* Search hiding every row replaces the whole table, but an
                  * empty submission keeps the column headers on screen —
                  * two different frames in the Figma, deliberately. */}
                {state.status === "ready" && isSearching && noMatches ? (
                    <TableEmptyState
                        icon={Folder}
                        // The same folder illustration Triage's "No Results
                        // Found" uses — Andressa's export for this screen
                        // turned out to be byte-for-byte that asset, so it
                        // stays one file rather than two copies.
                        illustration={
                            <img src={folderIcon} alt="" aria-hidden="true" className="size-12" />
                        }
                        title="No Results Found"
                        description="Try a different search"
                        data-autotest="invoice-no-results"
                    />
                ) : (
                    state.status === "ready" && (
                        <InvoiceHistoryTable
                            invoices={invoices}
                            search={search}
                            onAction={onRowAction}
                            archiveEnabled
                            emptyOverlay={
                                <TableEmptyState
                                    icon={File05}
                                    illustration={<InvoiceEmptyIcon />}
                                    title="Create an Invoice"
                                    description="Use the button below to start building the Invoice"
                                    addLabel="Create an Invoice"
                                    onAdd={createDisabled ? undefined : goCreate}
                                    data-autotest="invoice-empty-state"
                                />
                            }
                        />
                    )
                )}
            </section>
        </div>
    );
}
